import React, { useState, useEffect, useMemo } from "react";
import {
  FolderOpen,
  Plus,
  Search,
  ArrowUpDown,
  Text,
  Clock,
  Trash2,
  CircleDashed,
  CheckCircle2,
  Ban as BanIcon,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  LogOut,
  RotateCcw,
  ShieldAlert,
  Briefcase,
  Activity,
  AlertCircle,
  Info,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../config/firebase";
import { APP_ID } from "../config/constants";
import { resolveCaseStatus } from "../config/statusMapping";
import { logAuditAction } from "../services/auditService";
import AuditLogPanel from "./AuditLogPanel";

export default function Dashboard({ user, onNavigate }: any) {
  const [cases, setCases] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<"updated" | "name">("updated");
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<"active" | "archived" | "trash">("active");
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [isAttentionCollapsed, setIsAttentionCollapsed] = useState(() =>
    localStorage.getItem("attentionCollapsed") === "true"
  );

  // Theme state
  const [darkMode] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("theme");
      if (saved) return saved === "dark";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // Data Fetching
  useEffect(() => {
    if (!user || !db) return;
    const casesRef = collection(
      db,
      "artifacts",
      APP_ID,
      "public",
      "data",
      "onboarding_cases",
    );

    const unsubscribe = onSnapshot(
      casesRef,
      (snapshot) => {
        const fetchedCases = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCases(fetchedCases);
      },
      (error) => {
        console.error("Error fetching cases:", error);
      },
    );
    return () => unsubscribe();
  }, [user]);

  // AUTO-UPDATE: Check for expired meetings and update status in Firestore
  useEffect(() => {
    if (!cases || cases.length === 0) return;

    const checkAndPromoteCases = async () => {
      const now = Date.now();
      const updates = [];

      for (const c of cases) {
        // Only check cases that are explicitly "meeting_scheduled"
        if (c.status === "meeting_scheduled" && c.meeting?.date) {
          let meetingTime = 0;
          // Handle different date formats (number vs string)
          if (typeof c.meeting.date === "number") {
            meetingTime = c.meeting.date;
          } else if (typeof c.meeting.date === "string") {
            meetingTime = new Date(c.meeting.date).getTime();
          }

          // If meeting time has passed (buffer of 1 minute to be safe)
          if (meetingTime > 0 && meetingTime < now) {
            updates.push(c.id);
          }
        }
      }

      // Perform updates
      if (updates.length > 0) {
        console.log(`Auto-promoting ${updates.length} cases to 'In Progress'`);
        await Promise.all(
          updates.map((id) =>
            updateDoc(
              doc(
                db,
                "artifacts",
                APP_ID,
                "public",
                "data",
                "onboarding_cases",
                id,
              ),
              {
                status: "busy", // 'busy' maps to 'In Progress'
                lastUpdated: serverTimestamp(),
              },
            ),
          ),
        );
      }
    };

    // Run check immediately and then every minute
    checkAndPromoteCases();
    const interval = setInterval(checkAndPromoteCases, 60000);
    return () => clearInterval(interval);
  }, [cases]);

  // Sorting & Filtering
  const filteredCases = useMemo(() => {
    let data = [...cases];

    // 1. Filter by Tab
    if (activeTab === "trash") {
      data = data.filter((c) => c.deleted === true);
    } else if (activeTab === "archived") {
      data = data.filter((c) => !c.deleted && c.status === "transferred");
    } else {
      // active tab — exclude deleted and archived
      data = data.filter((c) => {
        if (c.deleted) return false;
        if (c.status === "transferred") return false;
        return true;
      });
    }

    // 2. Search
    if (searchTerm) {
      data = data.filter(
        (c) =>
          c.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.domain?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // 3. Sort
    if (sortOrder === "name") {
      data.sort((a, b) =>
        (a.clientName || "").localeCompare(b.clientName || ""),
      );
    } else {
      data.sort(
        (a, b) => (b.lastUpdated?.seconds || 0) - (a.lastUpdated?.seconds || 0),
      );
    }
    return data;
  }, [cases, searchTerm, sortOrder, activeTab]);

  const handleDeleteCase = async (e: any, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Move this case to trash?")) return;
    try {
      const docRef = doc(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
        id,
      );
      await updateDoc(docRef, {
        deleted: true,
        lastUpdated: serverTimestamp(),
      });

      const targetCase = cases.find((c) => c.id === id);
      logAuditAction(
        "DELETE",
        {
          collection: "onboarding_cases",
          id,
          name: targetCase?.clientName || "Unknown",
        },
        { uid: user?.uid || "unknown", email: user?.email || "unknown" },
        [{ field: "deleted", oldValue: false, newValue: true }],
      );
      toast.success("Case moved to trash");
    } catch (err) {
      console.error(err);
      toast.error("Failed to move case to trash");
    }
  };

  const handleRestoreCase = async (e: any, id: string) => {
    e.stopPropagation();
    try {
      const docRef = doc(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
        id,
      );
      await updateDoc(docRef, {
        deleted: false,
        lastUpdated: serverTimestamp(),
      });

      const targetCase = cases.find((c) => c.id === id);
      logAuditAction(
        "RESTORE",
        {
          collection: "onboarding_cases",
          id,
          name: targetCase?.clientName || "Unknown",
        },
        { uid: user?.uid || "unknown", email: user?.email || "unknown" },
        [{ field: "deleted", oldValue: true, newValue: false }],
      );
      toast.success("Case restored");
    } catch (err) {
      console.error(err);
      toast.error("Failed to restore case");
    }
  };

  const handlePermanentDelete = async (e: any, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Permanently delete this case? This CANNOT be undone."))
      return;
    try {
      const targetCase = cases.find((c) => c.id === id);
      await deleteDoc(
        doc(db, "artifacts", APP_ID, "public", "data", "onboarding_cases", id),
      );

      logAuditAction(
        "DELETE",
        {
          collection: "onboarding_cases",
          id,
          name: targetCase?.clientName || "Unknown",
        },
        { uid: user?.uid || "unknown", email: user?.email || "unknown" },
        undefined,
        { permanent: true },
      );
      toast.success("Case permanently deleted");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete case");
    }
  };

  const handleStatusChange = async (e: any, id: string, newStatus: string) => {
    e.stopPropagation();
    try {
      const targetCase = cases.find((c) => c.id === id);
      const oldStatus = targetCase?.status || "new";

      const docRef = doc(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
        id,
      );
      await updateDoc(docRef, {
        status: newStatus,
        lastUpdated: serverTimestamp(),
      });

      logAuditAction(
        "STATUS_CHANGE",
        {
          collection: "onboarding_cases",
          id,
          name: targetCase?.clientName || "Unknown",
        },
        { uid: user?.uid || "unknown", email: user?.email || "unknown" },
        [{ field: "status", oldValue: oldStatus, newValue: newStatus }],
      );
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const getStatusUI = (s: string) => {
    switch (s) {
      case "meeting_scheduled":
        return {
          label: "Meeting Set",
          icon: <Calendar className="w-3 h-3" />,
          color:
            "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
        };
      case "busy":
        return {
          label: "In Progress",
          icon: <Clock className="w-3 h-3" />,
          color:
            "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
        };
      case "on_hold":
        return {
          label: "On Hold",
          icon: <BanIcon className="w-3 h-3" />,
          color:
            "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
        };
      case "transferred":
        return {
          label: "Transferred",
          icon: <CheckCircle2 className="w-3 h-3" />,
          color:
            "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
        };
      case "new":
      default:
        return {
          label: "New",
          icon: <CircleDashed className="w-3 h-3" />,
          color:
            "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
        };
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "Never";
    return new Date(timestamp.seconds * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Stats Calculation
  const stats = useMemo(() => {
    const active = cases.filter(
      (c) =>
        !c.deleted && resolveCaseStatus(c).tab === "active",
    ).length;
    const archived = cases.filter(
      (c) => !c.deleted && c.status === "transferred",
    ).length;
    const stuck = cases.filter(
      (c) => !c.deleted && c.status !== "transferred" && resolveCaseStatus(c).column === "stuck",
    ).length;

    return [
      {
        label: "Active Cases",
        value: active,
        icon: Activity,
        color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
      },
      {
        label: "Stuck / On Hold",
        value: stuck,
        icon: AlertCircle,
        color: "text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
      },
      {
        label: "Archived",
        value: archived,
        icon: CheckCircle2,
        color: "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
      },
    ];
  }, [cases]);

  // Stale cases (active, not transferred, inactive > 7 days)
  const staleCases = useMemo(() => {
    return cases
      .filter((c) => {
        if (c.deleted || c.status === "transferred") return false;
        return resolveCaseStatus(c).isStale;
      })
      .sort((a, b) => (a.lastUpdated?.seconds || 0) - (b.lastUpdated?.seconds || 0));
  }, [cases]);

  const handleSnooze = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await updateDoc(
        doc(db, "artifacts", APP_ID, "public", "data", "onboarding_cases", id),
        { lastUpdated: serverTimestamp() }
      );
      toast.success("Snoozed for 7 days");
    } catch {
      toast.error("Failed to snooze");
    }
  };

  const toggleAttentionCollapsed = () => {
    const next = !isAttentionCollapsed;
    setIsAttentionCollapsed(next);
    localStorage.setItem("attentionCollapsed", String(next));
  };

  // Pipeline Logic (Kanban 2.0)
  const getPipelineStage = (c: any) => {
    return resolveCaseStatus(c).column;
  };

  const pipelineColumns = [
    {
      id: "awaiting_meeting",
      label: "Awaiting Meeting",
      color:
        "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
      description:
        "Cases that have been created but have no kickoff meeting scheduled yet.",
    },
    {
      id: "upcoming",
      label: "Upcoming",
      color:
        "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800",
      description: "Kickoff meeting is booked and scheduled for a future date.",
    },
    {
      id: "in_progress",
      label: "In Progress",
      color:
        "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800",
      description: "Meeting complete. Checklist is actively being worked on.",
    },
    {
      id: "stuck",
      label: "Stuck / Blocked",
      color:
        "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
      description:
        "Stalled cases (>7 days inactivity) or cases with explicit blockers.",
    },
    {
      id: "review",
      label: "Ready for Review",
      color:
        "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800",
      description:
        "Progress is > 90%. Case is ready for final review and handover.",
    },
  ];

  return (
    <div
      className="min-h-screen font-sans bg-gray-50 dark:bg-slate-900 transition-colors duration-200 w-full overflow-x-hidden"
      onClick={() => setIsSortMenuOpen(false)}
    >
      {/* STICKY HEADER (Glassmorphism) */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b border-gray-200/50 dark:border-slate-700/50 sticky top-0 z-50 transition-colors duration-200 supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-slate-700/50 rounded-lg text-blue-600 dark:text-blue-400">
              <FolderOpen size={20} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-bold text-slate-800 dark:text-white leading-tight">
                Case Manager
              </h1>
              <span className="text-[10px] text-gray-500 dark:text-slate-400 font-medium">
                {cases.length} Total Projects
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="h-6 w-px bg-gray-200 dark:bg-slate-700 mx-1"></div>
            <button
              onClick={() => setIsAuditOpen(true)}
              className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white p-2 transition-colors"
              title="Audit Logs"
            >
              <ShieldAlert size={18} />
            </button>
            <button
              onClick={() => auth.signOut()}
              className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white p-2 transition-colors"
              title="Sign Out"
            >
              <LogOut size={18} />
            </button>

            <button
              onClick={() => onNavigate("new")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02] ml-2"
            >
              <Plus size={16} /> New Case
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div
        className="mx-auto p-4 md:p-8 space-y-8 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-[1920px] px-6"
      >
        {/* STATS OVERVIEW */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-100 dark:border-slate-700/50 shadow-sm flex items-center justify-between"
            >
              <div>
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${stat.color}`}>
                <stat.icon size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* NEEDS ATTENTION STRIP */}
        {activeTab === "active" && staleCases.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl overflow-hidden">
            <button
              onClick={toggleAttentionCollapsed}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-500" />
                <span className="text-sm font-bold text-amber-700 dark:text-amber-400">
                  {staleCases.length} case{staleCases.length > 1 ? "s" : ""} need attention
                </span>
                <span className="text-xs text-amber-500 dark:text-amber-500 font-medium">
                  — no activity in 7+ days
                </span>
              </div>
              {isAttentionCollapsed
                ? <ChevronDown size={16} className="text-amber-500 flex-shrink-0" />
                : <ChevronUp size={16} className="text-amber-500 flex-shrink-0" />
              }
            </button>

            {!isAttentionCollapsed && (
              <div className="flex gap-3 overflow-x-auto px-4 pb-4 snap-x">
                {staleCases.map((c) => {
                  const days = c.lastUpdated
                    ? Math.floor((Date.now() - c.lastUpdated.seconds * 1000) / (1000 * 60 * 60 * 24))
                    : null;
                  return (
                    <div
                      key={c.id}
                      onClick={() => onNavigate(c.id)}
                      className="flex-shrink-0 w-52 bg-white dark:bg-slate-800 rounded-lg border border-amber-200 dark:border-amber-800/50 p-3 cursor-pointer hover:border-amber-400 dark:hover:border-amber-600 hover:shadow-md transition-all snap-start"
                    >
                      <div className="font-bold text-sm text-slate-800 dark:text-white truncate mb-0.5">
                        {c.clientName || "Untitled"}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate mb-3">
                        {c.domain || "No domain"}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-amber-600 dark:text-amber-400">
                          {days !== null ? `${days}d ago` : "Unknown"}
                        </span>
                        <button
                          onClick={(e) => handleSnooze(e, c.id)}
                          className="text-[11px] font-bold px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-400 transition-colors"
                          title="Snooze for 7 days"
                        >
                          Snooze
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TABS & FILTERS CONTAINER */}
        <div className="space-y-6">
          {/* TABS */}
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-slate-800 pb-1">
            {[
              { id: "active", label: "Active", icon: Briefcase },
              { id: "archived", label: "Archived", icon: CheckCircle2 },
              { id: "trash", label: "Trash", icon: Trash2 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-bold transition-all border-b-2
                ${
                  activeTab === tab.id
                    ? "text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-900/10"
                    : "text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800/50"
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* FILTERS & SEARCH */}
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients or domains..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm outline-none focus:ring-2 focus:ring-blue-500/50 text-sm transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="relative w-full md:w-auto flex items-center gap-2">
              <div className="relative w-full md:w-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSortMenuOpen(!isSortMenuOpen);
                  }}
                  className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 shadow-sm text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors w-full md:w-48"
                >
                  <span className="flex items-center gap-2">
                    <ArrowUpDown size={14} />{" "}
                    {sortOrder === "name" ? "Name (A-Z)" : "Last Updated"}
                  </span>
                  <ChevronDown size={14} className="opacity-50" />
                </button>

                {isSortMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden z-20 animate-in fade-in zoom-in-95">
                    <button
                      onClick={() => {
                        setSortOrder("name");
                        setIsSortMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm flex items-center gap-2 border-b dark:border-slate-700 ${sortOrder === "name" ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" : "text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"}`}
                    >
                      <Text size={14} /> Name (A-Z)
                    </button>
                    <button
                      onClick={() => {
                        setSortOrder("updated");
                        setIsSortMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm flex items-center gap-2 ${sortOrder === "updated" ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" : "text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"}`}
                    >
                      <Clock size={14} /> Recently Updated
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT AREA - BOARD ONLY */}
        {activeTab === "archived" || activeTab === "trash" ? (
          filteredCases.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-400 dark:text-slate-600">
              {activeTab === "trash" ? <Trash2 size={40} className="mb-3 opacity-30" /> : <CheckCircle2 size={40} className="mb-3 opacity-30" />}
              <p className="font-bold text-sm">{activeTab === "trash" ? "Trash is empty" : "No archived cases"}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCases.map((c) => {
                const p = c.totalItems > 0 ? Math.round((c.progress / c.totalItems) * 100) : 0;
                const isTrash = activeTab === "trash";
                return (
                  <div
                    key={c.id}
                    onClick={() => !isTrash && onNavigate(c.id)}
                    className={`bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm opacity-70 hover:opacity-100 transition-all relative group ${isTrash ? "border border-red-100 dark:border-red-900/30" : "border border-gray-200 dark:border-slate-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:-translate-y-0.5"}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm line-clamp-1">
                        {c.clientName || "Untitled"}
                      </h4>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${isTrash ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" : "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"}`}>
                        {isTrash ? "Deleted" : "Archived"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-3 truncate">
                      {c.domain || "No Domain"}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 bg-gray-100 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-slate-300 dark:bg-slate-600"
                          style={{ width: `${p}%` }}
                        ></div>
                      </div>
                      <span className="text-[10px] font-bold text-gray-400">{p}%</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>{formatTime(c.lastUpdated)}</span>
                      {isTrash && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => handleRestoreCase(e, c.id)}
                            className="p-1.5 text-gray-300 hover:text-green-500 dark:text-slate-600 dark:hover:text-green-400 transition-colors rounded-full hover:bg-green-50 dark:hover:bg-green-900/20"
                            title="Restore"
                          >
                            <RotateCcw size={14} />
                          </button>
                          <button
                            onClick={(e) => handlePermanentDelete(e, c.id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400 transition-colors rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Delete Permanently"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* KANBAN BOARD VIEW - PIPELINE MODE */
          <div className="flex overflow-x-auto pb-8 gap-6 snap-x snap-mandatory">
            {pipelineColumns.map((column) => (
              <div
                key={column.id}
                className="flex-shrink-0 w-80 flex flex-col snap-center"
              >
                {/* Column Header */}
                <div
                  className={`flex items-center justify-between p-3 rounded-lg border mb-3 ${column.color}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {column.label}
                    </span>
                    <div className="group relative">
                      <Info
                        size={14}
                        className="cursor-help opacity-50 hover:opacity-100 transition-opacity"
                      />
                      <div className="absolute left-0 top-full mt-2 w-48 p-3 bg-slate-800 text-white text-[11px] rounded-lg shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50 border border-slate-600 leading-relaxed">
                        {column.description}
                        {/* Arrow pointing up */}
                        <div className="absolute left-3 -top-1 w-2 h-2 bg-slate-800 border-t border-l border-slate-600 transform rotate-45"></div>
                      </div>
                    </div>
                  </div>
                  <span className="bg-white/50 dark:bg-black/20 px-2 py-0.5 rounded text-[10px] font-bold">
                    {
                      filteredCases.filter(
                        (c) => getPipelineStage(c) === column.id,
                      ).length
                    }
                  </span>
                </div>

                {/* Cards Container */}
                <div className="flex-1 space-y-3 min-h-[200px]">
                  {filteredCases
                    .filter((c) => getPipelineStage(c) === column.id)
                    .map((c) => {
                      const p =
                        c.totalItems > 0
                          ? Math.round((c.progress / c.totalItems) * 100)
                          : 0;
                      const resolved = resolveCaseStatus(c);
                      const allInvoiced =
                        Array.isArray(c.purchasedProducts) &&
                        c.purchasedProducts.length > 0 &&
                        c.purchasedProducts.every((prod: any) => prod.invoiced === true);
                      return (
                        <div
                          key={c.id}
                          onClick={() => onNavigate(c.id)}
                          className={`p-4 rounded-xl border shadow-sm hover:shadow-md cursor-pointer transition-all hover:-translate-y-0.5 duration-200 relative group ${
                            allInvoiced
                              ? "bg-green-50 dark:bg-green-900/10 border-green-400 dark:border-green-600 hover:border-green-500 dark:hover:border-green-500"
                              : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex flex-col gap-1">
                              <h4 className="font-bold text-slate-800 dark:text-white text-sm line-clamp-1">
                                {c.clientName || "Untitled"}
                              </h4>
                              {resolved.isStale && (
                                <span className="w-fit px-1.5 py-0.5 rounded text-[8px] font-black bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800 animate-pulse">
                                  STALE
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {allInvoiced && (
                                <span className="text-[9px] font-bold uppercase bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-1.5 py-0.5 rounded border border-green-200 dark:border-green-700">
                                  Invoiced
                                </span>
                              )}
                              {c.serviceType === "audit" ? (
                                <span className="text-[9px] font-bold uppercase bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                  Audit
                                </span>
                              ) : (
                                <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded">
                                  Impl
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mb-3 truncate">
                            {c.domain || "No Domain"}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${p === 100 ? "bg-green-500" : "bg-blue-500"}`}
                                style={{ width: `${p}%` }}
                              ></div>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400">
                              {p}%
                            </span>
                          </div>
                          <div className="mt-3 flex items-center justify-between text-[10px] text-gray-400">
                            <div className="flex items-center gap-1">
                              <User size={10} />
                              <span className="truncate max-w-[80px]">
                                {c.contactName ||
                                  c.contactPerson ||
                                  c.billing?.details?.contactPerson ||
                                  "Unknown"}
                              </span>
                            </div>
                            <span>{formatTime(c.lastUpdated)}</span>

                            <div className="flex items-center">
                              <button
                                onClick={(e) => handleDeleteCase(e, c.id)}
                                className="p-1.5 -mr-1 text-gray-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400 transition-colors rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Move to Trash"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          {/* Manual "Mark as Started" Action for Awaiting Meeting cases */}
                          {column.id === "awaiting_meeting" && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (
                                  !window.confirm(
                                    "Mark this case as started? This will set the meeting date to today and move it to 'In Progress'.",
                                  )
                                )
                                  return;
                                try {
                                  const docRef = doc(
                                    db,
                                    "artifacts",
                                    APP_ID,
                                    "public",
                                    "data",
                                    "onboarding_cases",
                                    c.id,
                                  );
                                  // Set meeting date to NOW to move it to In Progress
                                  await updateDoc(docRef, {
                                    meeting: {
                                      date: Date.now(),
                                      link: "",
                                      attendeeName: "Manual Override",
                                      attendeeEmail: "",
                                    },
                                    has_meeting: true,
                                    lastUpdated: serverTimestamp(),
                                    status: "busy", // also ensure status is busy
                                  });
                                  toast.success("Case marked as started");
                                } catch (err) {
                                  console.error(err);
                                  toast.error("Failed to update case");
                                }
                              }}
                              className="absolute top-9 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-50 text-teal-600 dark:bg-teal-900/50 dark:text-teal-400 text-[10px] font-bold px-2 py-1 rounded border border-teal-100 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900"
                            >
                              Mark Started
                            </button>
                          )}{" "}
                        </div>
                      );
                    })}{" "}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AuditLogPanel
        isOpen={isAuditOpen}
        onClose={() => setIsAuditOpen(false)}
      />
    </div>
  );
}
